import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../admin/AuthContext";
import { ASSETS } from "../i18n/translations";
import { LayoutDashboard, Inbox, Wallet, MapPin, LogOut, ExternalLink } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Übersicht", icon: LayoutDashboard, end: true },
  { to: "/admin/inquiries", label: "Anfragen", icon: Inbox },
  { to: "/admin/models", label: "Servicemodelle", icon: Wallet },
  { to: "/admin/contact-info", label: "Kontaktdaten", icon: MapPin },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-layout" className="min-h-screen flex bg-[#fafbfd] text-[#0A0A0A]">
      <aside className="hidden md:flex flex-col w-64 lg:w-72 bg-white border-r border-[#1d428a]/15 sticky top-0 h-screen">
        <Link to="/admin" className="flex items-center gap-3 px-5 py-5 border-b border-[#1d428a]/15">
          <img src={ASSETS.logo} alt="Logo" className="h-12 w-auto" />
          <div className="leading-tight">
            <div className="font-display-mid text-base text-[#1d428a] tracking-wide">SCHWEIZER</div>
            <div className="font-mono-tech text-[#df4661] mt-0.5 text-[10px]">PLANMANUFAKTUR · ADMIN</div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 font-mono-tech transition-colors ${
                    isActive
                      ? "bg-[#1d428a] text-white"
                      : "text-[#5A6270] hover:bg-[#e8ecf3] hover:text-[#1d428a]"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.8} />
                {item.label.toUpperCase()}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 pb-3 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 font-mono-tech text-[#5A6270] hover:bg-[#e8ecf3] hover:text-[#1d428a]"
          >
            <ExternalLink size={16} strokeWidth={1.8} />
            WEBSEITE
          </a>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-logout"
            className="w-full text-left flex items-center gap-3 px-3 py-2.5 font-mono-tech text-[#5A6270] hover:bg-[#df4661] hover:text-white transition-colors"
          >
            <LogOut size={16} strokeWidth={1.8} />
            ABMELDEN
          </button>
        </div>

        <div className="px-5 py-4 border-t border-[#1d428a]/15 bg-[#e8ecf3]/50">
          <div className="font-mono-tech text-[#5A6270]">ANGEMELDET</div>
          <div className="font-display-mid text-sm text-[#1d428a] mt-1 truncate">{user?.email}</div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center justify-between p-4 border-b border-[#1d428a]/15 bg-white">
          <Link to="/admin" className="flex items-center gap-2">
            <img src={ASSETS.logo} alt="Logo" className="h-10 w-auto" />
            <span className="font-display-mid text-[#1d428a]">ADMIN</span>
          </Link>
          <button
            onClick={onLogout}
            className="font-mono-tech text-[#df4661]"
            data-testid="admin-logout-mobile"
          >
            ABMELDEN
          </button>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
